import { Box } from '@mui/material';

import { Sector } from '@/domain/impact/impact.entity';
import { deleteImpactCo2Answer } from '@/domain/impact-co2/actions/delete-impact-co2-answer.action';
import { submitImpactCo2Answer } from '@/domain/impact-co2/actions/submit-impact-co2-answer.action';
import {
  Category,
  ImpactCo2Answer,
} from '@/domain/impact-co2/entities/question.entity';
import { useAppDispatch } from '@/ui/hook/store';
import { BuildingHeatingForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/BuildingHeatingForm.tsx';
import { CargoForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/CargoForm.tsx';
import { OfficeSuppliesNewForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/OfficeSuppliesNewForm.tsx';
import { OfficeSuppliesRefurbishForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/OfficeSuppliesRefurbishForm.tsx';
import { ScenographyNoRecyclableForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/ScenographyNoRecyclableForm.tsx';
import { ScenographyRecyclableForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/ScenographyRecyclableForm.tsx';
import { TeamMobilityForm } from '@/ui/pages/app/components/quiz-panel/co2/components/forms/TeamMobilityForm.tsx';

import { AccommodationForm } from './components/forms/AccommodationForm';
import { BuildingForm } from './components/forms/BuildingForm';
import { CateringBeverageForm } from './components/forms/CateringBeverageForm';
import { CateringMealForm } from './components/forms/CateringMealForm';
import { DigitalForm } from './components/forms/DigitalForm';
import { EnergyForm } from './components/forms/EnergyForm';
import { EquipmentForm } from './components/forms/EquipmentForm';
import { GeneratorForm } from './components/forms/GeneratorForm';
import { MobilityForm } from './components/forms/MobilityForm';
import { MobilityPublicForm } from './components/forms/MobilityPublicForm';
import { NonRecyclableWasteForm } from './components/forms/NonRecyclableWasteForm';
import { RecyclableWasteForm } from './components/forms/RecyclableWasteForm';
import { WaterForm } from './components/forms/WaterForm';

export function ImpactForm({
  projectId,
  sector,
  category,
  isExternal = false,
}: {
  projectId: number;
  sector: Sector;
  category: Category;
  isExternal?: boolean;
}) {
  const dispatch = useAppDispatch();

  const onSubmit = (answer: ImpactCo2Answer) => {
    dispatch(
      submitImpactCo2Answer({ answer, projectId, submitMode: 'create' }),
    );
  };

  const onEdit = (answer: ImpactCo2Answer) => {
    dispatch(submitImpactCo2Answer({ answer, projectId, submitMode: 'edit' }));
  };

  const onDelete = (answer: ImpactCo2Answer) => {
    dispatch(deleteImpactCo2Answer({ answer: answer, projectId }));
  };

  switch (category) {
    case 'ACCOMMODATION':
      return (
        <AccommodationForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'BUILDING':
      return (
        <BuildingForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'CATERING_BEVERAGE':
      return (
        <CateringBeverageForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'CATERING_MEAL':
      return (
        <CateringMealForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'ENERGY':
      return (
        <EnergyForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'EQUIPMENT':
      return (
        <EquipmentForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'GENERATOR':
      return (
        <GeneratorForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'MOBILITY':
      return (
        <MobilityForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'MOBILITY_PUBLIC':
      return (
        <MobilityPublicForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
          eventPosition={undefined}
        />
      );
    case 'NON_RECYCLABLE_WASTE':
      return (
        <NonRecyclableWasteForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'RECYCLABLE_WASTE':
      return (
        <RecyclableWasteForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'WATER':
      return (
        <WaterForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'SCENOGRAPHY_NO_RECYCLABLE':
      return (
        <ScenographyNoRecyclableForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'SCENOGRAPHY_RECYCLABLE':
      return (
        <ScenographyRecyclableForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'BUILDING_HEATING':
      return (
        <BuildingHeatingForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'OFFICE_SUPPLIES_NEW':
      return (
        <OfficeSuppliesNewForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'OFFICE_SUPPLIES_REFURBISH':
      return (
        <OfficeSuppliesRefurbishForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'TEAM_MOBILITY':
      return (
        <TeamMobilityForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'DIGITAL':
      return (
        <DigitalForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    case 'CARGO':
      return (
        <CargoForm
          sector={sector}
          onDelete={onDelete}
          onSubmit={onSubmit}
          onEdit={onEdit}
          isExternal={isExternal}
        />
      );
    default:
      return <NoFormForThisCategory category={category} />;
  }
}

function NoFormForThisCategory({ category }: { category: Category }) {
  return (
    <Box
      sx={{
        height: 100,
        width: 300,
        p: 2,
        border: 'solid 3px white',
        backgroundColor: '#664432',
      }}
    >
      no form for category: {category}
    </Box>
  );
}
